import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useSocketStore } from '../stores/socketStore';
import { useAuthStore } from '../stores/authStore';
import { GAME_MODES, GameModeId } from '../../../shared/constants';
import { 
  Users, 
  Play, 
  Crown, 
  Send, 
  Copy, 
  Check,
  LogOut,
  Music,
  Loader2,
  AlertTriangle,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';

// Helper pour décoder les entités HTML
function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

export default function RoomPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { player, spotifyToken, isAuthenticated } = useAuthStore();
  const { 
    currentRoom, 
    messages, 
    isPlaying,
    leaveRoom, 
    sendMessage,
    startGame,
    joinRoom,
    hasLeftRoom,
  } = useSocketStore();
  
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaveConfirmStep, setLeaveConfirmStep] = useState(1); // 1 or 2 for double confirmation
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  
  // Scroll au dernier message
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);
  
  // Si on arrive sur cette page sans room, essayer de rejoindre
  // SAUF si on vient de quitter volontairement
  // Track if we've already attempted to join
  const joinAttemptedRef = useRef(false);
  
  useEffect(() => {
    // Si on a quitté volontairement, rediriger vers l'accueil
    if (hasLeftRoom) {
      navigate('/', { replace: true });
      return;
    }
    
    if (!currentRoom && code && !joinAttemptedRef.current) {
      joinAttemptedRef.current = true;
      joinRoom(code);
      
      // Timeout si la room ne charge pas après 5 secondes
      const timeout = setTimeout(() => {
        if (!currentRoom) {
          setLoadingTimeout(true);
        }
      }, 5000);
      
      return () => clearTimeout(timeout);
    }
  }, [currentRoom, code, joinRoom, hasLeftRoom, navigate]);
  
  // Reset join attempt when room is set
  useEffect(() => {
    if (currentRoom) {
      joinAttemptedRef.current = false;
    }
  }, [currentRoom]);
  
  // Rediriger si partie en cours
  useEffect(() => {
    if (isPlaying && code) {
      navigate(`/game/${code}`);
    }
  }, [isPlaying, code, navigate]);
  
  // Avertir avant de quitter la page (fermeture onglet, refresh)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentRoom) {
        e.preventDefault();
        e.returnValue = 'Vous êtes dans une room. Voulez-vous vraiment quitter ?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [currentRoom]);
  
  const handleCopyCode = () => {
    if (currentRoom) {
      navigator.clipboard.writeText(currentRoom.code);
      setCopied(true);
      toast.success('Code copié !');
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim()) {
      sendMessage(message.trim());
      setMessage('');
    }
  };
  
  const handleLeaveClick = () => {
    setLeaveConfirmStep(1);
    setShowLeaveConfirm(true);
  };
  
  const handleLeaveFirstConfirm = () => {
    setLeaveConfirmStep(2);
  };
  
  const handleLeaveConfirm = () => {
    leaveRoom();
    // Navigation is handled by the useEffect watching hasLeftRoom
    // Don't navigate here to avoid race condition with auto-rejoin
  };
  
  const handleLeaveCancel = useCallback(() => {
    setShowLeaveConfirm(false);
    setLeaveConfirmStep(1);
  }, []);
  
  // Fermer le modal avec Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showLeaveConfirm) {
        handleLeaveCancel();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showLeaveConfirm, handleLeaveCancel]);
  
  const handleStart = async () => {
    if (!startGame) {
      toast.error('Fonctionnalité en cours de développement');
      return;
    }
    
    setIsStarting(true);
    
    try {
      // Charger les tracks de la playlist
      const playlistId = currentRoom?.playlistId;
      
      if (playlistId && spotifyToken) {
        // shuffle=true pour un offset aléatoire dans les grosses playlists
        const response = await fetch(`/api/playlists/${playlistId}/tracks?shuffle=true`, {
          headers: {
            'Authorization': `Bearer ${spotifyToken}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.tracks && data.tracks.length > 0) {
            startGame(data.tracks);
          } else {
            toast.error('Aucune piste jouable dans cette playlist');
            setIsStarting(false);
          }
        } else {
          toast.error('Erreur lors du chargement des pistes');
          setIsStarting(false);
        }
      } else {
        // Mode démo sans playlist
        startGame();
      }
    } catch (error) {
      console.error('Erreur chargement tracks:', error);
      toast.error('Erreur lors du chargement des pistes');
      setIsStarting(false);
    }
  };
  
  const isHost = currentRoom?.hostId === player?.id;

  if (!isAuthenticated || !player) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <h1 className="text-2xl font-bold mb-4">Connexion requise</h1>
        <p className="text-dark-400 mb-6">Vous devez être connecté pour accéder à cette page.</p>
        <Link to="/" className="btn-primary">Retour à l'accueil</Link>
      </div>
    );
  }

  if (!currentRoom) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        {loadingTimeout ? (
          <>
            <AlertTriangle className="w-16 h-16 text-warning mb-4" />
            <h2 className="font-display text-xl font-bold mb-2">Room introuvable</h2>
            <p className="text-dark-400 mb-6 text-center">
              La room n'existe pas ou vous n'êtes pas connecté.
            </p>
            <button
              onClick={() => navigate('/', { replace: true })}
              className="btn-primary"
            >
              Retour à l'accueil
            </button>
          </>
        ) : (
          <>
            <div className="loader mb-4"></div>
            <p className="text-dark-400">Chargement de la room...</p>
          </>
        )}
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto">
      {/* Header Room */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="font-display text-2xl font-bold">{currentRoom.name}</h1>
            <button
              onClick={handleCopyCode}
              className="flex items-center gap-1 bg-primary-500/20 text-primary-400 px-3 py-1 rounded-lg text-sm font-mono hover:bg-primary-500/30 transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {currentRoom.code}
            </button>
          </div>
          <p className="text-dark-400 text-sm flex items-center gap-2">
            <span className="inline-flex items-center gap-1 bg-dark-700 px-2 py-1 rounded-lg">
              {GAME_MODES[currentRoom.settings.mode as GameModeId]?.icon || '🎵'} 
              Mode {GAME_MODES[currentRoom.settings.mode as GameModeId]?.name || currentRoom.settings.mode}
            </span>
            <span>• {currentRoom.settings.roundCount} manches</span>
            <span>• {currentRoom.settings.roundDuration}s</span>
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {isHost && (
            <button 
              onClick={handleStart}
              className="btn-success flex items-center gap-2"
              disabled={currentRoom.players.length < 1 || isStarting}
            >
              {isStarting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Chargement...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Lancer la partie
                </>
              )}
            </button>
          )}
          <button
            onClick={handleLeaveClick}
            className="btn-secondary flex items-center gap-2"
          >
            <LogOut className="w-5 h-5" />
            Quitter
          </button>
        </div>
      </div>
      
      {/* Modal de confirmation de sortie - Double confirmation */}
      <AnimatePresence>
        {showLeaveConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleLeaveCancel}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="card max-w-md w-full p-6"
              onClick={(e) => e.stopPropagation()}
            >
              {leaveConfirmStep === 1 ? (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-orange-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">Quitter la room ?</h3>
                      <p className="text-dark-400 text-sm">Première confirmation</p>
                    </div>
                    <button 
                      onClick={handleLeaveCancel}
                      className="ml-auto p-1 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <p className="text-dark-300 mb-6">
                    {isHost 
                      ? "En tant qu'hôte, si vous quittez, un autre joueur deviendra l'hôte."
                      : "Vous allez quitter cette room."
                    }
                  </p>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleLeaveCancel}
                      className="flex-1 btn-secondary"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleLeaveFirstConfirm}
                      className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-xl transition-colors"
                    >
                      Continuer
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-red-400">Confirmation finale</h3>
                      <p className="text-dark-400 text-sm">Cette action est irréversible</p>
                    </div>
                    <button 
                      onClick={handleLeaveCancel}
                      className="ml-auto p-1 hover:bg-white/10 rounded-lg transition-colors"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  
                  <p className="text-dark-300 mb-6">
                    <strong className="text-red-400">Êtes-vous vraiment sûr ?</strong><br />
                    Vous perdrez votre place dans cette partie et ne pourrez pas revenir automatiquement.
                  </p>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleLeaveCancel}
                      className="flex-1 btn-secondary"
                    >
                      Non, rester
                    </button>
                    <button
                      onClick={handleLeaveConfirm}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-4 rounded-xl transition-colors"
                    >
                      Oui, quitter définitivement
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Liste des joueurs */}
        <div className="card">
          <h2 className="font-semibold flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-primary-400" />
            Joueurs ({currentRoom.players.length}/{currentRoom.settings.maxPlayers})
          </h2>
          
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            <AnimatePresence>
              {currentRoom.players.map((p, index) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ delay: index * 0.05 }}
                  className="flex items-center gap-3 p-2 rounded-lg bg-white/5"
                >
                  {p.avatar ? (
                    <img src={p.avatar} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-sm font-bold">
                      {p.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="flex-1 truncate">{p.name}</span>
                  {p.id === currentRoom.hostId && (
                    <Crown className="w-4 h-4 text-yellow-500" />
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          
          {currentRoom.players.length === 0 && (
            <p className="text-dark-500 text-sm text-center py-4">
              En attente de joueurs...
            </p>
          )}
        </div>
        
        {/* Chat */}
        <div className="lg:col-span-2 card flex flex-col h-[500px]">
          <h2 className="font-semibold flex items-center gap-2 mb-4">
            <Music className="w-5 h-5 text-primary-400" />
            Chat
          </h2>
          
          {/* Messages */}
          <div 
            ref={chatRef}
            className="flex-1 overflow-y-auto space-y-2 mb-4"
          >
            {messages.length === 0 ? (
              <p className="text-dark-500 text-sm text-center py-8">
                Aucun message. Dites bonjour ! 👋
              </p>
            ) : (
              messages.map((msg) => (
                <div 
                  key={msg.id}
                  className={`flex items-start gap-2 ${msg.isSystem ? 'justify-center' : ''}`}
                >
                  {msg.isSystem ? (
                    <p className="text-dark-400 text-sm italic">{decodeHtmlEntities(msg.message || '')}</p>
                  ) : (
                    <>
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {(msg.playerName || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <span className="text-primary-400 text-sm font-medium">{msg.playerName || 'Joueur'}</span>
                        <p className="text-white text-sm">{decodeHtmlEntities(msg.message || '')}</p>
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
          
          {/* Input */}
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Votre message..."
              className="input-field flex-1"
              maxLength={500}
            />
            <button
              type="submit"
              className="btn-primary px-4"
              disabled={!message.trim()}
            >
              <Send className="w-5 h-5" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
